/**
 * K-T-12 — Layer-3 hook for canonical per-vehicle-model efficiency
 * within a chosen vehicle make (drill-down sibling of K-T-11's
 * `useVehicleMakeEfficiency`).
 *
 * Thin wrapper around `kpi_efficiency_by_vehicle_make_model(shop_id,
 * start_at, end_at, make)`. Returns one
 * `VehicleMakeModelEfficiencyResult` per vehicle.model with both
 * display totals and the canonical-filtered ratio + sample-gate
 * fields. Holds NO local KPI math.
 *
 * Replaces the local `loadModelDrilldown` aggregation in
 * `MakeModelPerformance.tsx`. Gated by `make`: hook is disabled when
 * `make` is null/undefined (drill-down collapsed).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fromZonedTime } from 'date-fns-tz';
import type { VehicleMakeModelEfficiencyResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UseVehicleMakeModelEfficiencyOptions {
  shopId: string;
  /** Shop-local `yyyy-MM-dd`. */
  startDate: string;
  /** Shop-local `yyyy-MM-dd`. */
  endDate: string;
  shopTimezone: string;
  /** The expanded vehicles.make. Hook is disabled when null/undefined. */
  make: string | null | undefined;
}

export interface UseVehicleMakeModelEfficiencyResult {
  data: VehicleMakeModelEfficiencyResult[] | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Stable query key:
 *   ['kpi', 'kpi_efficiency_by_vehicle_make_model', shopId, startDate,
 *    endDate, shopTimezone, make]
 */
export function useVehicleMakeModelEfficiency(
  opts: UseVehicleMakeModelEfficiencyOptions,
): UseVehicleMakeModelEfficiencyResult {
  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_efficiency_by_vehicle_make_model',
      opts.shopId,
      opts.startDate,
      opts.endDate,
      opts.shopTimezone,
      opts.make,
    ],
    queryFn: async (): Promise<VehicleMakeModelEfficiencyResult[] | null> => {
      const startAt = fromZonedTime(
        `${opts.startDate}T00:00:00`,
        opts.shopTimezone,
      ).toISOString();
      const endAt = fromZonedTime(
        `${opts.endDate}T23:59:59.999`,
        opts.shopTimezone,
      ).toISOString();

      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: {
          p_shop_id: string;
          p_start_at: string;
          p_end_at: string;
          p_make: string;
        },
      ) => Promise<{
        data: VehicleMakeModelEfficiencyResult[] | null;
        error: unknown;
      }>)('kpi_efficiency_by_vehicle_make_model', {
        p_shop_id: opts.shopId,
        p_start_at: startAt,
        p_end_at: endAt,
        p_make: opts.make as string,
      });

      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(
      opts.shopId &&
        opts.startDate &&
        opts.endDate &&
        opts.shopTimezone &&
        opts.make,
    ),
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
